import type { BreadcrumbProps } from '@blueprintjs/core'
import { observer } from 'mobx-react-lite'
import React, { useMemo } from 'react'
import { Filters, PerformanceFilters, TableName } from '../../config'
import { useKdpFilters } from '../../hooks/useKdpFilters'
import { useUser } from '../../hooks/useUser'
import type { Keyword } from '../../types/keyword'
import type { TableProps } from '../ad_groups/AdGroupsTable'
import { addExtraCols, spliceAfter } from '../campaigns/CampaignsTable'
import { Layout } from '../Layout'
import {
    Columns,
    defaultPerfColumns,
    type ExpandedPerformanceMetrics,
} from '../table2/ad_grid_columns'
import { type AgGridColumnDef, AgGridTable } from '../table2/AgGridTable'
import { useBidLimits } from '../bid_limits/useBidLimits'
import { OverviewCharts } from '../../pages/dashboard-overview-charts'
import { useAdManagerCustomFields } from '../../features/custom-fields/ad-manager'

const defaultFilters = [
    Filters.PortfolioId,
    Filters.CampaignId,
    Filters.AdGroupId,
    Filters.StateWithSplit,
    Filters.DeliveryStatus,
    Filters.DeliveryReason,
    Filters.KeywordMatchType,
    Filters.Bid,
    Filters.TargetACOS,
    ...PerformanceFilters,
]

const mutableFields: string[] = ['state', 'bid']

export const KeywordsTable = observer(
    ({
        nested,
        store,
        savedFiltersStore,
        tableHooks,
        showCharts = true,
    }: TableProps<Keyword & ExpandedPerformanceMetrics>): JSX.Element => {
        const useUserValues = useUser()
        const filters = useKdpFilters(defaultFilters)
        useBidLimits(store, 4, 'keyword_id')
        const customFields = useAdManagerCustomFields<Keyword & ExpandedPerformanceMetrics>({
            profileId: useUserValues.profile === null ? null : String(useUserValues.profile),
            entityType: 'target',
            entityIdKey: 'keyword_id',
            store,
            reload: tableHooks.reload,
        })

        const { params } = tableHooks

        const breadcrumbs: BreadcrumbProps[] = [{ text: 'Manage' }]

        const cols3 = useMemo(() => {
            return [...newColumns(useUserValues.is_kdp), ...customFields.columns]
        }, [customFields.columns, useUserValues.is_kdp])

        return (
            <Layout
                breadcrumbs={breadcrumbs}
                primary={TableName.Keyword}
                nested={nested}
                params={params}
            >
                {showCharts && (
                    <OverviewCharts params={params} className="mb-4" chartType="keywords" />
                )}
                <AgGridTable
                    name={TableName.Keyword}
                    columns={cols3}
                    tableHooks={tableHooks}
                    store={store}
                    showHeader={false}
                    savedFiltersStore={savedFiltersStore}
                    filters={filters}
                    selectKey="keyword_id"
                    stableRowId
                    search
                    searchPlaceholder="Search by keyword, ad group, or campaign"
                    columnSelection
                    mutableFields={mutableFields}
                    bulkActions
                    otherActionsLeft={customFields.bulkAction}
                    nested={nested}
                />
            </Layout>
        )
    },
)

const newColumns = <T,>(isKdp: boolean): AgGridColumnDef<T>[] => {
    const cols: AgGridColumnDef<T>[] = [
        Columns.Select,
        Columns.Keyword,
        Columns.Parent,
        Columns.ProductAdsCount,
        Columns.KeywordBid,
        Columns.EffectiveBid,
        Columns.BidLimits,
        Columns.LastBidChange,
        Columns.TopOfSearchImpressionShare,
        ...defaultPerfColumns,
    ]

    spliceAfter('campaign_name', cols, Columns.DeliveryStatus)
    addExtraCols(cols, isKdp)

    return cols
}
